import React, { useState, useRef } from 'react';
import { CanvasElement } from '../../stores/editorStore';
import { uploadImage } from '../../api/client';

interface ElementToolbarProps {
  element: CanvasElement;
  onUpdate: (changes: Partial<CanvasElement>) => void;
  onDelete: () => void;
}

export const ElementToolbar: React.FC<ElementToolbarProps> = ({ element, onUpdate, onDelete }) => {
  const [isChangingYouTube, setIsChangingYouTube] = useState(false);
  const [youtubeInput, setYoutubeInput] = useState(element.videoId || '');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFontSizeChange = (e: React.MouseEvent, delta: number) => {
    e.stopPropagation();
    e.preventDefault();
    const currentSize = element.fontSize || 16;
    const newSize = Math.max(8, Math.min(120, currentSize + delta));
    onUpdate({ fontSize: newSize });
  };

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate({ color: e.target.value });
  };

  const handleFileSelect = async (file: File) => {
    if (!file.type.startsWith('image/')) return;
    
    setIsUploading(true);
    try {
      const result = await uploadImage(file);
      onUpdate({ src: result.url });
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const parseYouTubeUrl = (input: string): { videoId: string; startTime?: number } => {
    // Try to extract video ID and start time from various YouTube URL formats
    let videoId = input.trim();
    let startTime: number | undefined;
    
    try {
      // Check if it's a URL
      if (input.includes('youtube.com') || input.includes('youtu.be')) {
        const url = new URL(input);
        
        // Handle youtu.be/VIDEO_ID format
        if (url.hostname === 'youtu.be') {
          videoId = url.pathname.slice(1); // Remove leading /
        }
        // Handle youtube.com/watch?v=VIDEO_ID format
        else if (url.searchParams.has('v')) {
          videoId = url.searchParams.get('v') || '';
        }
        
        // Extract start time from t parameter (supports t=38 or t=1m30s formats)
        const timeParam = url.searchParams.get('t');
        if (timeParam) {
          // Handle formats like "38", "1m30s", "90s"
          const match = timeParam.match(/^(\d+)$/);
          if (match) {
            startTime = parseInt(match[1]);
          } else {
            // Parse format like "1m30s" or "90s"
            const minMatch = timeParam.match(/(\d+)m/);
            const secMatch = timeParam.match(/(\d+)s/);
            startTime = (minMatch ? parseInt(minMatch[1]) * 60 : 0) + (secMatch ? parseInt(secMatch[1]) : 0);
          }
        }
      }
    } catch {
      // If URL parsing fails, treat input as video ID
      videoId = input.trim();
    }
    
    return { videoId, startTime };
  };

  const handleYouTubeSave = () => {
    const { videoId, startTime } = parseYouTubeUrl(youtubeInput);
    onUpdate({ videoId, startTime: startTime ?? element.startTime });
    setIsChangingYouTube(false);
  };

  const getTypeLabel = () => {
    switch (element.type) {
      case 'text': return 'Texte';
      case 'title-block': return 'Titre';
      case 'image': return 'Image';
      case 'youtube': return 'YouTube';
      default: return element.type;
    }
  };

  return (
    <div className="h-10 bg-gray-800 border-b border-gray-700 flex items-center px-4 gap-4">
      <span className="text-gray-400 text-sm">
        Sélectionné: <span className="text-white font-medium">{getTypeLabel()}</span>
      </span>

      <div className="flex-1 flex items-center gap-3">
        {/* Text/Title-block controls */}
        {(element.type === 'text' || element.type === 'title-block') && (
          <>
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => handleFontSizeChange(e, -2)}
                className="w-6 h-6 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm flex items-center justify-center"
              >
                -
              </button>
              <span className="text-white text-sm w-8 text-center">{element.fontSize || 16}</span>
              <button
                onClick={(e) => handleFontSizeChange(e, 2)}
                className="w-6 h-6 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm flex items-center justify-center"
              >
                +
              </button>
            </div>
            <input
              type="color"
              value={element.color || '#ffffff'}
              onChange={handleColorChange}
              className="w-8 h-6 bg-transparent border border-gray-600 rounded cursor-pointer"
              title="Couleur du texte"
            />
          </>
        )}

        {/* Image controls */}
        {element.type === 'image' && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileInputChange}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded disabled:opacity-50"
            >
              {isUploading ? 'Upload...' : 'Changer'}
            </button>
          </>
        )}

        {/* YouTube controls */}
        {element.type === 'youtube' && (
          <>
            {isChangingYouTube ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={youtubeInput}
                  onChange={(e) => setYoutubeInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleYouTubeSave();
                    if (e.key === 'Escape') setIsChangingYouTube(false);
                  }}
                  placeholder="URL ou ID YouTube"
                  className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm w-64"
                  autoFocus
                />
                <button
                  onClick={handleYouTubeSave}
                  className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded"
                >
                  OK
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  setYoutubeInput(element.videoId || '');
                  setIsChangingYouTube(true);
                }}
                className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded"
              >
                Changer vidéo
              </button>
            )}
            <div className="flex items-center gap-1">
              <span className="text-gray-400 text-sm">Début :</span>
              <input
                type="number"
                min="0"
                value={element.startTime || 0}
                onChange={(e) => onUpdate({ startTime: parseInt(e.target.value) || 0 })}
                className="w-16 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                title="Temps de début en secondes"
              />
              <span className="text-gray-500 text-xs">sec</span>
            </div>
          </>
        )}
      </div>

      <button
        onClick={onDelete}
        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded"
      >
        Supprimer
      </button>
    </div>
  );
};
